import { type MutableRefObject, useCallback, useState } from 'react'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { NewShapeTextSeedStyle } from '@/lib/shapes/types/new-shape-text-seed-style'
import { hitShapeIdAtClientPoint } from '../handlers/hit-shape-id-at-client-point'
import { isShapeTextSupported } from '@/lib/shapes/shape-text-frame'

/**
 * Inline shape-text editing as seen from the canvas: whether a session is live, whether a pointer
 * is over the frame being edited, and entering an edit on whatever shape is under the pointer.
 */
export function useShapeTextEditEntry({
  internalShapeManagerRef,
  newShapeTextSeedStyle,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  newShapeTextSeedStyle: NewShapeTextSeedStyle
}) {
  const [isShapeTextEditing, setIsShapeTextEditing] = useState(false)

  const isPointerOnEditingShapeText = useCallback(
    (clientX: number, clientY: number): boolean => {
      const shapeMgr = internalShapeManagerRef.current
      const editingId = shapeMgr?.getEditingShapeTextId()

      if (!editingId) return false

      const hitId = hitShapeIdAtClientPoint(clientX, clientY)

      if (hitId === editingId) return true

      const topEl = document.elementFromPoint(clientX, clientY)
      const overlay =
        topEl instanceof HTMLElement ? topEl.closest('[data-shape-text-overlay]') : null

      return overlay instanceof HTMLElement && overlay.getAttribute('data-shape-id') === editingId
    },
    [internalShapeManagerRef]
  )

  const tryEnterShapeTextEditFromPointer = useCallback(
    (clientX: number, clientY: number): boolean => {
      const shapeMgr = internalShapeManagerRef.current
      const domHitShapeId = hitShapeIdAtClientPoint(clientX, clientY)
      const fallbackHitShapeId = shapeMgr?.hitShapeIdAtClientPoint(clientX, clientY) ?? null
      const shapeId = domHitShapeId ?? fallbackHitShapeId

      if (!shapeId || !shapeMgr) return false

      const shape = shapeMgr.getShapes().find((s) => s.id === shapeId)

      if (!shape || shape.hidden || shape.locked || !isShapeTextSupported(shape)) {
        return false
      }

      if (shapeMgr.getEditingShapeTextId() === shapeId) {
        return false
      }

      shapeMgr.enterShapeTextEdit(shapeId, {
        caretClientPoint: { x: clientX, y: clientY },
        seedStyle: newShapeTextSeedStyle,
      })
      return true
    },
    [newShapeTextSeedStyle, internalShapeManagerRef]
  )

  return {
    isShapeTextEditing,
    setIsShapeTextEditing,
    isPointerOnEditingShapeText,
    tryEnterShapeTextEditFromPointer,
  }
}
